import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

/**
 * In-app replacement for the browser's confirm()/alert() popups.
 * Call showConfirm()/showAlert() from anywhere; <DialogHost /> (mounted once) renders them.
 */

interface DialogRequest {
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel?: string; // absent for alerts
  danger: boolean;
  resolve: (ok: boolean) => void;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions */
  danger?: boolean;
}

let pushRequest: ((req: DialogRequest) => void) | null = null;

export function showConfirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const req: DialogRequest = {
      title: options.title,
      message: options.message,
      confirmLabel: options.confirmLabel || 'ตกลง',
      cancelLabel: options.cancelLabel || 'ยกเลิก',
      danger: Boolean(options.danger),
      resolve,
    };
    if (pushRequest) pushRequest(req);
    else resolve(window.confirm([options.title, options.message].filter(Boolean).join('\n\n')));
  });
}

export function showAlert(title: string, message?: string): Promise<void> {
  return new Promise((resolve) => {
    const req: DialogRequest = { title, message, confirmLabel: 'ตกลง', danger: false, resolve: () => resolve() };
    if (pushRequest) pushRequest(req);
    else {
      window.alert([title, message].filter(Boolean).join('\n\n'));
      resolve();
    }
  });
}

export const DialogHost: React.FC = () => {
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const current = queue[0];

  useEffect(() => {
    pushRequest = (req) => setQueue((q) => [...q, req]);
    return () => {
      pushRequest = null;
    };
  }, []);

  const close = (ok: boolean) => {
    if (!current) return;
    current.resolve(ok);
    setQueue((q) => q.slice(1));
  };

  useEffect(() => {
    if (!current) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  if (!current) return null;

  const isAlert = current.cancelLabel === undefined;
  const Icon = current.danger ? AlertTriangle : Info;

  return (
    <div className="modal-overlay z-[60]">
      <div
        role="alertdialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="modal-panel max-w-sm"
      >
        <div className="p-5 flex gap-3.5">
          <div
            className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${
              current.danger ? 'bg-rose-500/15 text-rose-400' : 'bg-white/[0.06] text-zinc-300'
            }`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0 pt-1.5">
            <h3 className="text-[15px] font-semibold text-zinc-50 leading-snug">{current.title}</h3>
            {current.message && (
              <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{current.message}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          {!isAlert && (
            <button type="button" onClick={() => close(false)} className="btn btn-secondary">
              {current.cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            onClick={() => close(true)}
            className={`btn ${current.danger ? 'bg-rose-500 text-white hover:bg-rose-400' : 'btn-primary'}`}
          >
            {current.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
